/**
 * Which paper a printed sheet belongs to (bug fix 1, spec 8). Every printed page of a paper,
 * its mark scheme, the paper with answers and the answer sheets says so at its top right
 * ("PHYSICS · PAPER 3", 14pt bold, readable across a table) beside the Ref and the kind of
 * sheet, and again at its foot with the page count. A paper and its scheme carry identical
 * identifiers but for the kind, which is in words: print is often black and white.
 *
 * The repeat is the page's own margin boxes (`@page { @top-right … @bottom-center }`), so it
 * can never sit on a question; Chromium and Firefox draw them. Every printout's first page
 * also carries the tag in its flow (CoverTag), the floor in a browser that draws no margin
 * boxes. Print only: nothing changes on screen.
 */

export type SheetKind =
  | "Question paper"
  | "Mark scheme"
  | "Paper with answers"
  | "Answer sheet"
  | "Paper and mark scheme";

export type PaperIdentity = {
  subject: string;
  number: number;
  ref: string;
  name?: string | null;
  /** When it was asked for, as the cover prints it: "18 October 2026, 14:27". */
  made?: string;
};

/** "Physics · Paper 3". */
export const identityLine = (id: PaperIdentity) => `${id.subject} · Paper ${id.number}`;

/** A CSS string literal: quotes and backslashes escaped, no line breaks. */
const css = (text: string) =>
  `"${text.replace(/\\/g, "\\\\").replace(/"/g, '\\"').replace(/\s+/g, " ")}"`;

/** The page rule for this printout: the tag at the top right of every page, the foot line. */
export function pageRule(id: PaperIdentity, kind: SheetKind): string {
  const head = `${id.subject} · Paper ${id.number}`.toUpperCase();
  const side = `Ref ${id.ref} · ${kind}`;
  const foot = `${identityLine(id)} · Ref ${id.ref} · ${kind} · Page `;
  return `@media print {
  @page {
    margin: 26mm 16mm 18mm;
    @top-right {
      content: ${css(head)} "\\A" ${css(side)};
      white-space: pre;
      font: 14pt/1.25 Arial, "Helvetica Neue", Helvetica, sans-serif;
      font-weight: 700;
      color: #000;
      text-align: right;
      vertical-align: bottom;
      padding-bottom: 3mm;
    }
    @bottom-center {
      content: ${css(foot)} counter(page) " of " counter(pages);
      font: 8.5pt/1.2 Arial, "Helvetica Neue", Helvetica, sans-serif;
      color: #000;
    }
  }
}`;
}

/** The page rule, rendered while this printout is the one being printed. */
export function PrintPages({ identity, kind }: { identity: PaperIdentity; kind: SheetKind }) {
  return <style data-testid="print-pages">{pageRule(identity, kind)}</style>;
}

/** The boxed tag at the top right of a printout's first page, in its flow. */
export function CoverTag({ identity, kind }: { identity: PaperIdentity; kind: SheetKind }) {
  return (
    <div className="print-tag" data-testid="print-tag">
      <div className="print-tag__id">{identityLine(identity).toUpperCase()}</div>
      {kind === "Mark scheme" && <div className="print-tag__kind">{kind.toUpperCase()}</div>}
      <div className="print-tag__ref">Ref {identity.ref}</div>
      {kind !== "Mark scheme" && <div className="print-tag__kind">{kind}</div>}
    </div>
  );
}
